import React, { useState, useRef, useEffect } from 'react';
import { ProductEntity, ProductsListingOverview } from 'src/modules/products';
import { GlobalBubbleIcon, IconImage } from 'src/modules/global';
import { LayoutCardSize } from 'src/modules/layout';
import { ImageEntity } from '../../types';
import { useWindowSize } from 'src/hooks/useWindowSize';
import styles from './ProductContext.module.scss';

interface ProductTag {
  product: ProductEntity;
  x: number;
  y: number;
}

export interface ProductContextProps {
  image: ImageEntity;
  products: ProductTag[];
}

const ProductContext: React.FunctionComponent<ProductContextProps> = (
  props: ProductContextProps
) => {
  const { image, products: productTags = [] } = props;
  const [visibleCard, setVisibleCard] = useState(-1);
  const [hack, setHack] = useState('');
  useWindowSize();
  useEffect(() => setHack('hack'), []);

  const imageRef = useRef(null);

  const _onImageClick = (e) => {
    const classClicked = e.target.className;
    if (
      typeof e.target.className === 'string' &&
      (classClicked.includes('cardsContainer') || classClicked.includes('ant-row'))
    ) {
      setVisibleCard(-1);
    }
  };

  const getIconPosition = (x, y) => {
    if (!imageRef.current) {
      return {};
    }

    return {
      left: (x * imageRef.current.offsetWidth) / 100 - 28,
      top: (y * imageRef.current.offsetHeight) / 100 - 28,
    };
  };

  const getProductTagPosition = (x, y) => {
    const position: { left?: number; right?: number; bottom?: number; top?: number } = {};
    const proportionalX = (x * imageRef.current.offsetWidth) / 100 - 28;
    const proportionalY = (y * imageRef.current.offsetHeight) / 100 - 28;

    // width of the Product Card is 1/3 of the product image
    const productCardWidth = imageRef.current?.offsetWidth / 3;

    const padding = 18;

    //Check if product card overflows the image
    //76 + 24 + 10 = height of the card body. productCardWidth = height of the product card image
    const productCardHeight = 76 + 24 + 10 + productCardWidth;
    //Overflow bottom
    if (imageRef.current?.offsetHeight - proportionalY < productCardHeight / 2) {
      position.bottom = -40 + padding; // -40 = margin and padding of the product row
    }
    //Overflow top
    else if (proportionalY - productCardHeight / 2 < 0) {
      position.top = padding; // -40 = margin and padding of the product row
    } else {
      position.top = proportionalY + padding - productCardHeight / 2;
    }

    //Overflows on the right side
    if (imageRef.current?.offsetWidth - proportionalX < productCardWidth) {
      position.left = imageRef.current?.offsetWidth - productCardWidth + 10;
    }
    //Overflows on the left side
    else if (proportionalX - productCardWidth / 2 < 0) {
      position.left = padding;
    } else {
      position.left = proportionalX + 40 - productCardWidth / 2;
    }
    return position;
  };

  return (
    <div className={styles.container} key={`ProductContext-${hack}`}>
      <img ref={imageRef} src={image.url} />
      {image.caption && (
        <div className={styles.caption} dangerouslySetInnerHTML={{ __html: image.caption }} />
      )}
      <div className={styles.cardsContainer} onClick={_onImageClick}>
        {productTags.map((productTag, idx) => {
          if (!productTag?.product) return null;
          return (
            <div key={idx}>
              <GlobalBubbleIcon
                style={getIconPosition(productTag.x, productTag.y)}
                className={styles.icon}
                iconProps={{ image: IconImage.Plus }}
                onClick={() => setVisibleCard(idx)}
                onMouseOver={() => setVisibleCard(idx)}
              />
              {visibleCard === idx && (
                <div
                  style={getProductTagPosition(productTag.x, productTag.y)}
                  className={styles.card}
                  onMouseOut={() => {
                    setVisibleCard(-1);
                  }}>
                  <ProductsListingOverview
                    size={LayoutCardSize.Small}
                    headingTitle="page/professionals/projects/details/related_products"
                    headingMore={false}
                    oneRow={true}
                    relatedOverview={false}
                    products={[productTag.product]}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductContext;
